'use client';

import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useTranslations } from 'next-intl';
import { RouteIntlScope } from 'lib/i18n/routeIntl';
import styles from './MagicModalNew.module.css';

export const CrossLight = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="31"
    height="31"
    viewBox="0 0 31 31"
    fill="none"
  >
    <rect
      x="0.640625"
      y="0.373047"
      width="30"
      height="30"
      rx="15"
      fill="#F2F2F7"
    />
    <path
      d="M10.688 18.5551C10.377 18.8661 10.3706 19.4184 10.6943 19.7421C11.0244 20.0658 11.5766 20.0595 11.8813 19.7548L15.6391 15.997L19.3906 19.7485C19.708 20.0658 20.2539 20.0658 20.5776 19.7421C20.9013 19.412 20.9013 18.8725 20.584 18.5551L16.8325 14.8036L20.584 11.0458C20.9013 10.7284 20.9077 10.1825 20.5776 9.85884C20.2539 9.53514 19.708 9.53514 19.3906 9.85254L15.6391 13.6039L11.8813 9.85254C11.5766 9.54144 11.018 9.52874 10.6943 9.85884C10.3706 10.1825 10.377 10.7411 10.688 11.0458L14.4394 14.8036L10.688 18.5551Z"
      fill="#3C3C43"
      fillOpacity="0.6"
    />
  </svg>
);

const initialModalContext = {
  set: () => {},
  clear: () => {},
  show: () => {},
  hide: () => {},
  setCloseButtonTheme: () => {},
  setCloseBtnHidden: () => {},
};

export const MagicModalNewContext =
  createContext<Record<string, (...args: any) => void>>(initialModalContext);

export const MagicModalNewProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState<
    React.ReactNode | string | null
  >(null);
  const [lightCloseButton, setLightCloseButton] = useState(false);
  const [closeBtnHidden, setCloseBtnHidden] = useState<boolean>(false);

  const set = useCallback((content: string | React.ReactNode) => {
    setModalContent(content);
  }, []);

  const onHide = (fn: () => void) => {
    if (typeof fn === 'function') {
      fn();
    }
  };

  const hide = useCallback((fn?: () => void) => {
    setModalVisible(false);
    setLightCloseButton(false);
    setCloseBtnHidden(false);
    if (fn && typeof fn === 'function') {
      onHide(fn);
    }
  }, []);

  const clear = useCallback(() => {
    setModalContent(null);
    hide();
  }, [hide]);

  const show = useCallback(() => {
    setModalVisible(true);
  }, []);

  const setCloseButtonTheme = useCallback((theme: 'light' | 'dark') => {
    if (theme == 'dark') return;
    setLightCloseButton(true);
  }, []);

  const contextValue = useMemo(
    () => ({
      set,
      clear,
      show,
      hide,
      setCloseButtonTheme,
      setCloseBtnHidden,
    }),
    [set, clear, show, hide, setCloseButtonTheme],
  );

  return (
    <MagicModalNewContext.Provider value={contextValue}>
      {children}
      {isModalVisible && (
        <MagicModalNew
          lightCloseButton={lightCloseButton}
          hideModal={hide}
          closeBtnHidden={closeBtnHidden}
        >
          <RouteIntlScope>{modalContent}</RouteIntlScope>
        </MagicModalNew>
      )}
    </MagicModalNewContext.Provider>
  );
};

export const MagicModalNew = ({
  className,
  children,
  hideModal,
  closeBtnHidden = false,
}: {
  className?: string;
  children?: React.ReactNode | string | null;
  hideModal?: () => void;
  lightCloseButton?: boolean;
  closeBtnHidden?: boolean;
}) => {
    const t = useTranslations('common');
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Handle escape key to close modal
    useEffect(() => {
      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && hideModal) {
          hideModal();
        }
      };

      // Store the currently focused element before opening modal
      if (document.activeElement instanceof HTMLElement) {
        previousActiveElement.current = document.activeElement;
      }

      document.addEventListener('keydown', handleEscapeKey);

      // Focus the close button when modal opens (if it exists)
      if (closeButtonRef.current && !closeBtnHidden) {
        closeButtonRef.current.focus();
      }

      return () => {
        document.removeEventListener('keydown', handleEscapeKey);

        // Restore focus to the previously focused element when modal closes
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }, [hideModal, closeBtnHidden]);

    // Handle close button click
    const handleCloseClick = () => {
      if (hideModal) {
        hideModal();
      }
    };

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      aria-hidden="true"
      onClick={hideModal}
      onScroll={(e) => e.stopPropagation()}
    >
      <div className={styles.modalContainer}>
        <div className={styles.modal} onScroll={(e) => e.stopPropagation()}>
          <>
            <div id="modal-title" className={styles.srOnly}>
              {t('magicModalNew.content.modalContent')}
            </div>
            <div id="modal-description" className={styles.srOnly}>
              {t('magicModalNew.content.modalDialogContent')}
            </div>
            {children}
            {!closeBtnHidden && (
              <div
                className={styles.btnContainer}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="button"
                tabIndex={0}
                aria-label={t(
                  'magicModalNew.content.ariaLabel.closeButtonContainer',
                )}
              >
                <button
                  className="closeBtn"
                  onClick={handleCloseClick}
                  ref={closeButtonRef}
                  aria-label={t('magicModalNew.button.ariaLabel.closeModal')}
                  type="button"
                >
                  <span className={styles.srOnly}>
                    {t('magicModalNew.text.closeModal')}
                  </span>
                  {CrossLight}
                </button>
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  );
};
